import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Chip
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { BASE_URL } from "../../Api/ApiUrls";


const AddonTable = ({ 
  onAddClick, 
  onEditClick, 
  onDeleteClick,
  refreshFlag,
  setRefreshFlag 
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const fetchAddOnPlans = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/add-on/`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching add-on plans:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (addonId, currentStatus) => {
    try {
      setUpdatingStatus(addonId);
      const newStatus = !currentStatus;
      
      const response = await fetch(`${BASE_URL}/add-on/${addonId}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Update local state immediately for better UX
      setData(prevData => 
        prevData.map(item => 
          item.addOn_id === addonId ? { ...item, status: newStatus } : item
        )
      );
      
      // Refresh data to ensure consistency with backend
      setRefreshFlag(prev => !prev);
    } catch (err) {
      console.error("Error updating status:", err);
      setError(err.message);
    } finally {
      setUpdatingStatus(null);
    }
  };
  const handleExport = () => {
  if (!data || data.length === 0) {
    alert("No data to export!");
    return;
  }

  // Prepare data for Excel
  const exportData = data.map((item, index) => ({
    "S.No": index + 1,
    "User Type": item.user_type,
    "Charges": item.charges,
    "Actual Charge": item.actual_price,
    "Status": item.status ? "Active" : "Inactive",
    "Accessing Owner / Uploading Properties": 
      item.user_type === 'Buyer' || item.user_type === 'Tenant' 
        ? (item.no_of_properties_unlimited ? 'Unlimited' : item.no_of_properties)
        : (item.no_of_properties_unlimited ? 'Unlimited' : item.no_of_properties),
    "Wishlist Limit": item.no_of_liked_data_unlimited ? 'Unlimited' : item.no_of_liked_data,
    "Matching Buyers": item.matching_enquiry_unlimited ? 'Unlimited' : item.matching_enquiry,
  }));

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "AddonPlans");

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(dataBlob, "AddonPlans.xlsx");
};


  useEffect(() => {
    fetchAddOnPlans();
  }, [refreshFlag]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading add-on plans: {error}
      </Alert>
    );
  }

  return (
    <>
<Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>

    <Button 
      variant="contained" 
      color="success" 
      onClick={handleExport}
      sx={{ mr: 1 }}
    >
      Export 
    </Button>

    <Button 
      variant="contained" 
      color="primary" 
      startIcon={<AddIcon />}
      onClick={onAddClick}
    >
      Add AddOn Plan
    </Button>

</Box>


      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: '#1976d2' }}>
            <TableRow>
              <TableCell sx={{ color: 'white' }}>S.No</TableCell>
              {/* <TableCell sx={{ color: 'white' }}>Name</TableCell> */}
              <TableCell sx={{ color: 'white' }}>User Type</TableCell>
              <TableCell sx={{ color: 'white' }}>Charges</TableCell>
              <TableCell sx={{ color: 'white' }}>Actual Charge</TableCell>
              <TableCell sx={{ color: 'white' }}>Status</TableCell>
              <TableCell sx={{ color: 'white' }}>Features</TableCell>
              <TableCell sx={{ color: 'white' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => (
              <TableRow key={item.addOn_id}>
                <TableCell>{index + 1}</TableCell>
                {/* <TableCell>{item.name}</TableCell> */}
                <TableCell>{item.user_type}</TableCell>
                <TableCell>₹{item.charges}</TableCell>
                <TableCell>₹{item.actual_price}</TableCell>
                <TableCell>
                  <Chip 
                    icon={item.status ? <ToggleOnIcon /> : <ToggleOffIcon />}
                    label={item.status ? 'Active' : 'Inactive'} 
                    color={item.status ? 'success' : 'error'} 
                    size="small"
                    clickable
                    disabled={updatingStatus === item.addOn_id}
                    onClick={() => toggleStatus(item.addOn_id, item.status)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                  />
                  {updatingStatus === item.addOn_id && (
                    <CircularProgress size={20} sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: 400 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box>
                      {item.user_type === 'Buyer' || item.user_type === 'Tenant' ? (
                        <strong>Accessing Owner Details:</strong>
                      ) : (
                        <strong>Uploading Properties:</strong>
                      )}
                      {item.no_of_properties_unlimited ? ' Unlimited' : ` ${item.no_of_properties}`}
                    </Box>
                    <Box>
                      <strong>Wishlist Limit:</strong> {item.no_of_liked_data_unlimited ? 'Unlimited' : item.no_of_liked_data}
                    </Box>
                    <Box>
                      <strong>Matching Buyers:</strong> {item.matching_enquiry_unlimited ? 'Unlimited' : item.matching_enquiry}
                    </Box>
                    <Box sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 1 }}>
                      Created: {new Date(item.created_at).toLocaleString()}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <IconButton 
                    color="primary" 
                    onClick={() => onEditClick(item)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    color="error"
                    onClick={() => onDeleteClick(item)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default AddonTable;